import axios from 'axios';
import { sso } from '../../auth/sso';

/**
 * Creates an axios instance with internal-SSO authentication and standardized error handling
 * @param {string} baseURL - The base URL for all API requests
 * @param {object} scopes - inert: presence marks the call authenticated; the
 *   Stytch session token is scope-agnostic. Kept as a no-op arg for the ~37 callers.
 * @returns {axios.AxiosInstance} Configured axios instance with interceptors
 */
const createAxiosInstance = (baseURL, scopes) => {
  const instance = axios.create({
    baseURL,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  // Add request interceptor for authorization (internal-SSO session token).
  instance.interceptors.request.use(
    async (config) => {
      if (scopes) {
        const accessToken = await sso.getToken();
        if (!accessToken) {
          // No token: ask the guard where to go rather than assuming "log in". During a
          // sign-out the token is gone BY DESIGN and a redirect is already in flight, so
          // minting an authorize url here would supersede it and silently restore the
          // session. The guard returns the logout url in that case and the authorize url
          // otherwise, so one call covers both without this interceptor having to tell
          // them apart — and, critically, without a second read of the sign-out latch,
          // which is consuming: reading it here would take it from the route guard.
          const result = await sso.checkAuthenticated(window.location.href);
          if (!result.authenticated) {
            window.location.assign(result.redirectUrl);
          }
          return Promise.reject(new Error('No internal-SSO session'));
        }
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Transient-safe 401 handling: runs BEFORE the error-standardizing interceptor below (axios
  // response interceptors run in REVERSE registration order, so this registers first and
  // sees the raw 401). sso.onUnauthorized() tries a silent refresh first, then a bounded
  // consecutive-401 streak before conceding to interactive re-authorize — a lone transient
  // 401 never revokes the session.
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Deliberately NOT gated on a sign-out check: sso.onUnauthorized() reads the latch
      // itself before doing anything. A check here would consume it first and leave the
      // route guard to treat a sign-out as an ordinary sessionless load.
      if (error.response?.status === 401 && scopes) {
        await sso.onUnauthorized();
      }
      return Promise.reject(error);
    }
  );

  /**
   * Response interceptor that:
   * 1. Extracts response.data for successful requests (200-299 status codes)
   * 2. Standardizes error handling for multiple response formats:
   *    - Custom ApiResponse structure (with Message/Error fields)
   *    - Plain string responses (e.g., BadRequest("message"))
   *    - Standard .NET ProblemDetails
   *    - Generic object errors
   *
   * @typedef {Object} StandardizedError
   * @property {string} message - Primary error message (user-friendly or technical)
   * @property {*} apiResponse - Full API error response (string, object, etc.)
   * @property {string|null} userMessage - User-friendly message (only for object responses)
   * @property {string|null} technicalError - Technical error details (only for object responses)
   * @property {number} status - HTTP status code (400, 500, etc.)
   * @property {string} statusText - HTTP status text ("Bad Request", "Internal Server Error", etc.)
   *
   * Success Response Structure:
   * - Returns unwrapped response.data directly
   *
   * Error Response Structures Handled:
   * 1. Custom ApiResponse: { Data, Message, Error }
   *    - error.message = Message or Error field
   *    - error.userMessage = Message field
   *    - error.technicalError = Error field
   *
   * 2. Plain String: "Error message"
   *    - error.message = the string itself
   *
   * 3. Standard ProblemDetails: { title, detail, status }
   *    - error.message = title field
   *
   * 4. Generic Object: { error: "..." } or { message: "..." }
   *    - error.message = extracted from common error fields
   *
   * @example
   * // Success - Custom ApiResponse
   * // API returns: { Data: [...], Message: "Success", Error: null }
   * // Component receives: { Data: [...], Message: "Success", Error: null }
   *
   * @example
   * // Error - Custom ApiResponse
   * // API returns 500: { Data: null, Message: "User message", Error: "Technical details" }
   * // Component catches: error.message = "User message"
   * //                    error.technicalError = "Technical details"
   *
   * @example
   * // Error - Plain String
   * // API returns 400: "Invalid email format"
   * // Component catches: error.message = "Invalid email format"
   *
   * @example
   * // Error - ProblemDetails
   * // API returns 400: { title: "Validation failed", errors: {...} }
   * // Component catches: error.message = "Validation failed"
   */
  instance.interceptors.response.use(
    (response) => response.data,
    (error) => {
      if (error.response?.data) {
        const apiError = error.response.data;

        // Determine the error message based on response type
        let errorMessage;

        if (typeof apiError === 'string') {
          // Handle plain string responses (e.g., BadRequest("message"))
          errorMessage = apiError;
        } else if (typeof apiError === 'object') {
          // Handle custom ApiResponse or standard object errors
          errorMessage =
            apiError.message || // Custom ApiResponse.Message (camelCase)
            apiError.Message || // Custom ApiResponse.Message (PascalCase)
            apiError.error || // Custom ApiResponse.Error (camelCase)
            apiError.Error || // Custom ApiResponse.Error (PascalCase)
            apiError.title || // Standard ProblemDetails.Title
            apiError.Title || // Standard ProblemDetails.Title (PascalCase)
            JSON.stringify(apiError); // Last resort: stringify the object
        } else {
          // Fallback for any other type
          errorMessage = String(apiError);
        }

        const standardizedError = new Error(errorMessage || error.message);

        // Preserve everything for flexible access
        standardizedError.apiResponse = apiError;

        // Only set these if apiError is an object
        if (typeof apiError === 'object') {
          standardizedError.userMessage = apiError.message || apiError.Message || null;
          standardizedError.technicalError = apiError.error || apiError.Error || null;
        }

        standardizedError.status = error.response.status;
        standardizedError.statusText = error.response.statusText;

        return Promise.reject(standardizedError);
      }

      // No response data (network error, timeout, etc.)
      return Promise.reject(error);
    }
  );

  return instance;
};

/**
 * Converts kebab-case strings to camelCase
 * @param {string} str - String in kebab-case format (e.g., "foo-bar")
 * @returns {string} String in camelCase format (e.g., "fooBar")
 * @example
 * kebabCaseToCamel('secure-file-uploader') // returns 'secureFileUploader'
 */
function kebabCaseToCamel(str) {
  return str.replace(/(-\w)/g, (matches) => matches[1].toUpperCase());
}

/**
 * API3 class for creating and managing REST API endpoints with dynamic CRUD operations
 * @class
 * @example
 * const api = new API3({
 *   url: 'https://api.example.com',
 *   scopes: { scopes: ['api://your-scope'] }
 * });
 * api.createEntity({ name: 'users' });
 * const users = await api.endpoints.users.get()();
 */
class API3 {
  /**
   * Creates an API3 instance
   * @param {Object} config - Configuration object
   * @param {string} config.url - Base URL for the API
   * @param {Object} [config.scopes] - MSAL scopes for authentication
   */
  constructor({ url, scopes }) {
    this.url = url;
    this.endpoints = {};
    this.scopes = scopes;
    this.axios = createAxiosInstance(url, scopes);
  }

  /**
   * Creates and stores CRUD endpoints for a single entity
   * @param {Object} entity - Entity configuration
   * @param {string} entity.name - Entity name (will be converted from kebab-case to camelCase)
   * @param {string} [entity.route] - Optional custom route (defaults to entity.name)
   * @example
   * api.createEntity({ name: 'secure-file-uploader' });
   * // Creates api.endpoints.secureFileUploader with CRUD methods
   */
  createEntity(entity) {
    const name = kebabCaseToCamel(entity.name);
    this.endpoints[name] = this.createBasicCRUDEndpoints(entity.route ? entity.route : entity.name);
  }

  /**
   * Creates entities for multiple endpoints at once
   * @param {Array<Object>} arrayOfEntity - Array of entity configuration objects
   * @example
   * api.createEntities([
   *   { name: 'users' },
   *   { name: 'secure-file-uploader', route: 'files' }
   * ]);
   */
  createEntities(arrayOfEntity) {
    arrayOfEntity.forEach(this.createEntity.bind(this));
  }

  /**
   * Creates basic CRUD endpoint handlers for an entity
   * @param {string} name - The resource name for the URL path
   * @param {string} [params] - Optional query parameters to append to all requests
   * @param {string} [url] - Optional custom base URL (overrides instance URL)
   * @param {boolean} [textResponse=false] - If true, returns text instead of JSON
   * @returns {Object} Object containing CRUD methods (get, post, put, patch, delete, etc.)
   */
  createBasicCRUDEndpoints(name, params, url, textResponse = false) {
    const endpoints = {};
    let resourceURL = url ? `${url}/${name}` : `/${name}`;

    if (params) {
      resourceURL = `${resourceURL}?${params}`;
    }

    /**
     * GET request to retrieve resource(s)
     * @returns {Function} Async function that executes the GET request
     * @example
     * const getUsers = api.endpoints.users.get();
     * const users = await getUsers();
     */
    endpoints.get = () => async () => {
      const response = await this.axios.get(resourceURL, {
        responseType: textResponse ? 'text' : 'json',
      });
      return response;
    };

    /**
     * GET request with URI parameter
     * @param {string} uri - URI segment to append to the resource URL
     * @returns {Function} Async function that executes the GET request
     * @example
     * const getUser = api.endpoints.users.getWithURI('123');
     * const user = await getUser();
     */
    endpoints.getWithURI = (uri) => async () => {
      const response = await this.axios.get(`${resourceURL}/${uri}`, {
        responseType: textResponse ? 'text' : 'json',
      });
      return response;
    };

    /**
     * GET request with multiple query string parameters
     * @param {Array<{queryParam: string, queryVal: string}>} queries - Array of query parameter objects
     * @returns {Function} Async function that executes the GET request
     * @example
     * const getFilteredUsers = api.endpoints.users.getMultipleQueryString([
     *   { queryParam: 'status', queryVal: 'active' },
     *   { queryParam: 'role', queryVal: 'admin' }
     * ]);
     * const users = await getFilteredUsers();
     */
    endpoints.getMultipleQueryString = (queries) => async () => {
      const queryString = queries.map((q) => `${q.queryParam}=${q.queryVal}`).join('&');
      const response = await this.axios.get(`${resourceURL}?${queryString}`);
      return response;
    };

    /**
     * POST request to create a resource
     * @param {Object} body - Request body data
     * @param {Object} [options={}] - Additional axios config options
     * @param {Array<{queryParam: string, queryVal: string}>} [queries] - Optional query parameters
     * @returns {Function} Async function that executes the POST request
     * @example
     * const createUser = api.endpoints.users.post({ name: 'John', email: 'john@example.com' });
     * const newUser = await createUser();
     */
    endpoints.post =
      (body, options = {}, queries) =>
      async () => {
        const queryString = getQueryString(queries);
        const response = await this.axios.post(`${resourceURL}${queryString}`, body, {
          responseType: textResponse ? 'text' : 'json',
          ...options,
        });
        return response;
      };

    /**
     * POST request with FormData (typically for file uploads)
     * @param {FormData} data - FormData object containing files or form fields
     * @param {Array<{queryParam: string, queryVal: string}>} [queries] - Optional query parameters
     * @returns {Function} Async function that executes the POST request
     * @example
     * const formData = new FormData();
     * formData.append('file', fileBlob);
     * const uploadFile = api.endpoints.files.postData(formData, [
     *   { queryParam: 'userId', queryVal: '123' }
     * ]);
     * const result = await uploadFile();
     */
    endpoints.postData = (data, queries) => async () => {
      const queryString = queries?.map((q) => `${q.queryParam}=${q.queryVal}`).join('&');
      const response = await this.axios.post(`${resourceURL}?${queryString}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response;
    };

    /**
     * PATCH request to partially update a resource
     * @param {Object} data - Partial update data
     * @param {Array<{queryParam: string, queryVal: string}>} [queries] - Optional query parameters
     * @returns {Function} Async function that executes the PATCH request
     * @example
     * const updateUser = api.endpoints.users.patch(
     *   { email: 'newemail@example.com' },
     *   [{ queryParam: 'id', queryVal: '123' }]
     * );
     * const updated = await updateUser();
     */
    endpoints.patch = (data, queries) => async () => {
      const queryString = getQueryString(queries);
      const response = await this.axios.patch(`${resourceURL}${queryString}`, data);
      return response;
    };

    /**
     * PUT request to fully update/replace a resource
     * @param {Object} data - Complete resource data
     * @returns {Function} Async function that executes the PUT request
     * @example
     * const replaceUser = api.endpoints.users.put({ id: 123, name: 'John', email: 'john@example.com' });
     * const updated = await replaceUser();
     */
    endpoints.put = (data) => async () => {
      const response = await this.axios.put(resourceURL, data);
      return response;
    };

    /**
     * DELETE request to remove a resource
     * @param {string|number} [id] - Resource ID (currently unused in implementation)
     * @returns {Function} Async function that executes the DELETE request
     * @example
     * const deleteUser = api.endpoints.users.delete();
     * await deleteUser();
     */
    endpoints.delete = (id) => async () => {
      const response = await this.axios.delete(resourceURL);
      return response;
    };

    /**
     * DELETE request with URI path parameters
     * @param {Object} [pathParams={}] - Object with path parameter values
     * @returns {Function} Async function that executes the DELETE request
     * @example
     * const deleteUserFile = api.endpoints.files.deleteWithURI({ userId: '123', fileId: '456' });
     * // Makes DELETE request to /files/123/456
     * await deleteUserFile();
     */
    endpoints.deleteWithURI =
      (pathParams = {}) =>
      async () => {
        let url = resourceURL;
        if (Object.keys(pathParams).length > 0) {
          const path = Object.values(pathParams).map(encodeURIComponent).join('/');
          url += `/${path}`;
        }
        const response = await this.axios.delete(url);
        return response;
      };

    /**
     * POST request with multiple query string parameters
     * @param {Array<{queryParam: string, queryVal: string}>} queries - Array of query parameter objects
     * @param {Object} body - Request body data
     * @returns {Function} Async function that executes the POST request
     * @example
     * const createTask = api.endpoints.tasks.postMultipleQueryString(
     *   [
     *     { queryParam: 'userId', queryVal: '123' },
     *     { queryParam: 'priority', queryVal: 'high' }
     *   ],
     *   { title: 'New Task', description: 'Task details' }
     * );
     * const task = await createTask();
     */
    endpoints.postMultipleQueryString = (queries, body) => async () => {
      const queryString = queries.map((q) => `${q.queryParam}=${q.queryVal}`).join('&');
      const response = await this.axios.post(`${resourceURL}?${queryString}`, body);
      return response;
    };

    return endpoints;
  }
}

/**
 * Builds a query string from an array of query parameter objects
 * @param {Array<{queryParam: string, queryVal: string}>} [queries] - Array of query parameter objects
 * @returns {string} Query string starting with '?' or empty string if no queries
 * @example
 * getQueryString([
 *   { queryParam: 'page', queryVal: '1' },
 *   { queryParam: 'limit', queryVal: '10' }
 * ]) // returns '?page=1&limit=10'
 */
const getQueryString = (queries) => {
  if (!queries) return '';
  return `?${queries.map((q) => `${q.queryParam}=${q.queryVal}`).join('&')}`;
};

export default API3;
